'use client';

import { useState, useEffect } from 'react';
import { IndianRupee } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CurrencyInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
}

/**
 * Format number to Indian currency style (lakhs and crores)
 * E.g., 1500000 -> "15,00,000" or "15 Lakhs"
 */
function formatIndianCurrency(value: number): string {
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} Lakhs`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toLocaleString('en-IN');
}

/**
 * Parse Indian currency string to number
 * Handles inputs like "15L", "1.5Cr", "1500000"
 * DEFAULT: Plain numbers are treated as LAKHS (e.g., 15 = 15 Lakhs = 1500000)
 */
function parseIndianCurrency(value: string): number {
  const cleaned = value.replace(/[,\s₹]/g, '').toLowerCase();

  if (cleaned.includes('cr') || cleaned.includes('crore')) {
    const num = parseFloat(cleaned.replace(/[a-z]/g, ''));
    return num * 10000000;
  } else if (cleaned.includes('l') || cleaned.includes('lakh')) {
    const num = parseFloat(cleaned.replace(/[a-z]/g, ''));
    return num * 100000;
  } else if (cleaned.includes('k')) {
    const num = parseFloat(cleaned.replace(/[a-z]/g, ''));
    return num * 1000;
  }

  // Default: treat plain numbers as LAKHS
  // e.g., 15 = 15 lakhs = 1,500,000
  // e.g., 1.5 = 1.5 lakhs = 150,000
  const num = parseFloat(cleaned);
  if (num > 0 && num < 1000) {
    // Numbers under 1000 are assumed to be in lakhs
    return num * 100000;
  }

  return num || 0;
}

export function CurrencyInput({
  value,
  onChange,
  placeholder = 'Enter amount in Lakhs (e.g., 15 = 15L)',
  className,
  required = false,
  disabled = false,
  label,
}: CurrencyInputProps) {
  const [displayValue, setDisplayValue] = useState('');
  const [formattedPreview, setFormattedPreview] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  // Sync displayValue when value prop changes (e.g., when switching questions)
  useEffect(() => {
    if (!isFocused) {
      // Only update display when not focused to avoid overwriting user input
      if (value) {
        const numValue = parseFloat(value);
        if (numValue > 0) {
          setDisplayValue(formatIndianCurrency(numValue));
          setFormattedPreview(formatIndianCurrency(numValue));
        } else {
          setDisplayValue('');
          setFormattedPreview('');
        }
      } else {
        setDisplayValue('');
        setFormattedPreview('');
      }
    }
  }, [value, isFocused]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setDisplayValue(newValue);

    // Parse and send the numeric value
    const numValue = parseIndianCurrency(newValue);
    onChange(numValue > 0 ? numValue.toString() : newValue);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Format on blur for better UX
    const numValue = parseIndianCurrency(displayValue);
    if (numValue > 0) {
      setDisplayValue(formatIndianCurrency(numValue));
      setFormattedPreview(formatIndianCurrency(numValue));
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Clear for fresh input
    setDisplayValue('');
  };

  return (
    <div className={cn('space-y-1', className)}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <IndianRupee className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          className={cn(
            'w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-4',
            'text-sm text-foreground placeholder:text-muted-foreground',
            'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
            'disabled:bg-muted disabled:cursor-not-allowed'
          )}
        />
      </div>
      {formattedPreview && (
        <p className="text-xs text-green-600 dark:text-green-400 font-medium">
          = ₹{formattedPreview}
        </p>
      )}
      <p className="text-xs text-muted-foreground">
        💡 Enter in Lakhs: 15 = ₹15 Lakhs, 1.5 = ₹1.5 Lakhs
      </p>
    </div>
  );
}
